<template>
    <PageSection>
        <PageHeader title="Search" />
        <Card>
            <CatalogSearchHeader @search="handleSearch" @sort="handleSort" @filter="handleFilters" :search="searchText"
                :filters="filters" :sort-options="sortOptions" />
            <div class="grow overflow-y-auto border-t border-purple-100">
                <transition name="fade" mode="out-in">
                    <div v-if="!!resultCount" class="grow overflow-y-auto divide-y divide-purple-100">

                        <!-- These are the search results -->
                        <div class="relative group cursor-pointer rounded overflow-hidden hover:bg-#FBFAFF" v-for="bookDetails in state.context.results"
                            @click="handleSearchResultClick(bookDetails)">
                            <BookInformation :book="bookDetails" :show-prices="!isEAorIA"
                                class="p-6" />
                            <ArrowRightIcon class="hidden absolute top-6 right-6 c-gray-1000 group-hover:block" />
                        </div>

                        <ScrollPagination :showing-count="resultCount"
                            :total-count="state.context.metadata?.hitCounts ?? resultCount"
                            :can-show-more="resultCount !== state.context.metadata?.hitCounts"
                            :show-more="() => send('SHOW_MORE_CLICKED')" :loading-more="isLoading"
                            data-testid="catalog-search-pagination"
                            v-if="resultCount !== state.context.metadata?.hitCounts" />
                        <CallToAction v-else
                            text="Can't find what you're looking for? This doesn't mean we don't have it."
                            class="border-t border-purple-100">
                            <button class="btn btn-tertiary"
                                @click="router.push(getRequestPageRoute(institutionConfig.approval_config, isEAorIA))">
                                Make a request
                            </button>
                        </CallToAction>
                    </div>
                    <div v-else class="grow">
                        <LoadingSpinner v-show="isLoading" class="grow justify-center text-purple-500" />
                        <EmptyState v-if="!resultCount && !isLoading"
                            :icon="resultNotFound ? SearchFailIcon : SearchIcon"
                            :title="resultNotFound ? 'No results found' : 'Search for books'" :text="resultNotFound
                                ? 'Try searching again or adjusting your filter options.'
                                : 'Use the search controls above to find books to adopt'
                                " variant="plain" data-testid="catalog-search-empty-state"></EmptyState>
                        <CallToAction v-if="!resultCount && !isLoading"
                            text="Are you looking for courseware? Make a courseware request here."
                            class="border-t border-purple-100">
                            <button class="btn btn-tertiary"
                                @click="router.push(getRequestPageRoute(institutionConfig.approval_config, isEAorIA))">
                                Make a request
                            </button>
                        </CallToAction>
                    </div>
                </transition>
            </div>
        </Card>
    </PageSection>
</template>

<script setup lang="ts">
import { definePage } from "vue-router/auto";
import ArrowRightIcon from "../../../../shared/assets/icons/arrow-right.svg?component";
import type { BookDetails } from "../../../../shared/components/Book/BookInformation.vue";
import BookInformation from "../../../../shared/components/Book/BookInformation.vue";
import CallToAction from "../../../../shared/components/CallToAction.vue";
import EmptyState from "../../../../shared/components/EmptyState.vue";
import LoadingSpinner from "../../../../shared/components/LoadingSpinner.vue";
import PageSection from "../../../../shared/components/PageSection.vue";
import ScrollPagination from "../../../../shared/components/ScrollPagination.vue";
import SearchIcon from "../../assets/icons/search.svg?component";
import SearchFailIcon from "../../assets/icons/search-fail.svg?component";
import CatalogSearchHeader from "../../components/CatalogSearch/CatalogSearchHeader.vue";
import PageHeader from "../../components/PageHeader.vue";
import { useCatalogueSearch } from "../../composables/useCatalogueSearch";
import { useInstitution } from "../../composables/useInstitution";
import { Context } from "../../state-machines/catalogue-search";
import { FilterFields } from "../../trpc/routers/catalogue-search";
import { getRequestPageRoute } from "../../utils/request-page-route";

const { institutionConfig, isEAorIA } = useInstitution();

type Filter = Context["query"]["filters"];
type SortOptions = Context["query"]["sortOptions"];

const {
	send,
	state,
	isLoading,
	resultCount,
	resultNotFound,
	searchText,
	sortOptions,
	filters,
} = useCatalogueSearch();

const router = useRouter();

type SearchInput = {
	searchText: string;
	sortOptions?: Context["query"]["sortOptions"];
	filters?: Context["query"]["filters"];
};
function handleSearch(options: SearchInput) {
	const { searchText, sortOptions, filters } = options;
	if (searchText === "") {
		send("RESET");
		return;
	}

	send("SEARCH_SUBMITTED", {
		data: {
			searchText,
			sortOptions,
			filters,
		},
	});
}

function handleSort(newSortOptions?: SortOptions) {
	if (!hasSortChanged(sortOptions.value, newSortOptions)) {
		return;
	}
	handleSearch({
		sortOptions: newSortOptions,
		searchText: searchText.value,
		filters: filters.value,
	});
}

function handleFilters(newFilters: Filter) {
	if (!hasFilterChanged(filters.value, newFilters)) {
		return;
	}
	handleSearch({
		searchText: searchText.value,
		sortOptions: sortOptions.value,
		filters: newFilters,
	});
}

function hasFilterChanged(oldFilters?: Filter, newFilters?: Filter): boolean {
	if (!oldFilters || !newFilters) {
		return oldFilters !== newFilters;
	}

	const allKeys = new Set([
		...Object.keys(oldFilters),
		...Object.keys(newFilters),
	] as FilterFields[]);

	for (const key of allKeys) {
		const oldValue = oldFilters[key] || [];
		const newValue = newFilters[key] || [];

		if (oldValue.length !== newValue.length) {
			return true;
		}
		const oldSet = new Set(oldValue);
		const newSet = new Set(newValue);
		if (
			oldValue.some((item) => !newSet.has(item)) ||
			newValue.some((item) => !oldSet.has(item))
		) {
			return true;
		}
	}

	return false;
}

function hasSortChanged(oldSort?: SortOptions, newSort?: SortOptions): boolean {
	if (!oldSort || !newSort) return oldSort !== newSort;

	if (oldSort.length !== newSort.length) return true;

	// Compare each element at the same index
	for (let i = 0; i < oldSort.length; i++) {
		if (
			oldSort[i].sortColumn !== newSort[i].sortColumn ||
			oldSort[i].sortDirection !== newSort[i].sortDirection
		) {
			return true;
		}
	}

	return false;
}

function handleSearchResultClick(bookDetails: BookDetails) {
	router.push({
		name: "/search/[nbsi]",
		params: bookDetails.isbn
			? {
					nbsi: bookDetails.isbn,
				}
			: undefined,
	});
}

definePage({
	meta: {
		requiresAuth: true,
		permission: "catalogue:search",
	},
});
</script>
